import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import Header from "./Header";
import { Link } from "react-router-dom";
import Footer from "./Footer";

// 기업소개서 PDF (public/files/ir/propmate-ir-{lang}.pdf)
const IR_FILES = [
  { lang: 'ko', title: '기업소개서', label: '한국어' },
  { lang: 'en', title: 'Introduction', label: 'English' },
  { lang: 'zh', title: '公司介绍', label: '中文' },
  { lang: 'de', title: 'Firmenprofil', label: 'Deutsch' },
  { lang: 'fi', title: 'Yritysesittely', label: 'Suomi' },
  { lang: 'ar', title: 'ملف الشركة', label: 'العربية' },
];

function About(props) {
    const t = useT();
  const [modalState, setModalState] = useState(false);
  const fileWrapRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  useEffect(() => {
    props.checkLogin();
  }, []);

  // 기업소개서 목록 가로 스크롤: 휠→가로 변환 + 부드러운 이동 + 항상 보이는 맞춤 스크롤바
  useEffect(() => {
    const el = fileWrapRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;

    let target = el.scrollLeft;
    let raf = 0;
    const maxScroll = () => el.scrollWidth - el.clientWidth;
    const clamp = (v) => Math.max(0, Math.min(maxScroll(), v));

    // 스크롤바 위치·길이를 실제 스크롤에 맞춘다
    const sync = () => {
      const max = maxScroll();
      track.style.display = max > 0 ? '' : 'none';
      if (max <= 0) return;
      const trackW = track.clientWidth;
      const thumbW = Math.max(40, trackW * (el.clientWidth / el.scrollWidth));
      thumb.style.width = thumbW + 'px';
      thumb.style.transform = 'translateX(' + (el.scrollLeft / max) * (trackW - thumbW) + 'px)';
    };

    // target 까지 감속하며 이동
    const animate = () => {
      const diff = target - el.scrollLeft;
      if (Math.abs(diff) < 0.5) {
        el.scrollLeft = target;
        raf = 0;
        return;
      }
      el.scrollLeft += diff * 0.18;
      raf = requestAnimationFrame(animate);
    };
    const glideTo = (v) => {
      target = clamp(v);
      if (!raf) raf = requestAnimationFrame(animate);
    };

    // 세로 휠을 가로로. 목록 끝에 닿으면 페이지 스크롤로 넘긴다
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const max = maxScroll();
      if (max <= 0) return;
      const base = raf ? target : el.scrollLeft;
      if ((e.deltaY < 0 && base <= 0) || (e.deltaY > 0 && base >= max - 1)) return;
      e.preventDefault();
      glideTo(base + e.deltaY);
    };

    // 손가락·트랙패드로 직접 밀 때는 target 을 따라가게
    const onScroll = () => {
      if (!raf) target = el.scrollLeft;
      sync();
    };

    // 손잡이 드래그
    let drag = null;
    const onThumbDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      cancelAnimationFrame(raf);
      raf = 0;
      drag = { x: e.clientX, left: el.scrollLeft };
      thumb.setPointerCapture(e.pointerId);
      track.classList.add('dragging');
    };
    const onThumbMove = (e) => {
      if (!drag) return;
      const room = track.clientWidth - thumb.offsetWidth;
      if (room <= 0) return;
      el.scrollLeft = clamp(drag.left + (e.clientX - drag.x) * (maxScroll() / room));
      target = el.scrollLeft;
    };
    const onThumbUp = () => {
      drag = null;
      track.classList.remove('dragging');
    };

    // 트랙을 누르면 그 위치로 부드럽게
    const onTrackDown = (e) => {
      if (e.target === thumb) return;
      const rect = track.getBoundingClientRect();
      const room = track.clientWidth - thumb.offsetWidth;
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left - thumb.offsetWidth / 2) / room));
      glideTo(ratio * maxScroll());
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('scroll', onScroll, { passive: true });
    thumb.addEventListener('pointerdown', onThumbDown);
    thumb.addEventListener('pointermove', onThumbMove);
    thumb.addEventListener('pointerup', onThumbUp);
    thumb.addEventListener('pointercancel', onThumbUp);
    track.addEventListener('pointerdown', onTrackDown);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    sync();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', onScroll);
      thumb.removeEventListener('pointerdown', onThumbDown);
      thumb.removeEventListener('pointermove', onThumbMove);
      thumb.removeEventListener('pointerup', onThumbUp);
      thumb.removeEventListener('pointercancel', onThumbUp);
      track.removeEventListener('pointerdown', onTrackDown);
    };
  }, []);
  return (
    <div id="Company">
      <Header userInfo={props.userInfo} logout={props.logout} />
      <main>
        <div className="container">
          <h2 className="mobile_title">PROP.MATE</h2>
          <div className="image_container">
            <img src={process.env.PUBLIC_URL + 'images/company/company.png'} alt={t("건축물사진")} />
          </div>
          <div className="information_container">
            <div className="text_wrap">
              <h2>PROP.MATE</h2>
              <p> 
                <span>
                  {t('프롭메이트는 지도 기반으로')} <br />
                  {t('토지 관련 모든 정보를 신속하고 정확하게 제공하는 전문 서비스를 자랑합니다.')}
                </span>
                <span>
                  {t('강력한 데이터베이스와 AI 기술을 바탕으로,')}<br />
                  {t('고객이 원하는 건물을 지을 수 있는 규모와 예상 비용을 정밀하게 예측합니다.')}<br />
                  {t('또한, 건축 가능 용도에 따른 다양한 개발 옵션을 제시하며,')}<br />
                  {t('끊임없이 발전하는 기술과 정보를 통해 고객이 가장 현명한 결정을 내릴 수 있도록 지원합니다.')}
                </span>
                <span>
                  {t('항상 고객 만족을 최우선 가치로 삼으며,')}<br />
                  {t('함께하는 모든 건축 과정에서 안도감과 신뢰를 제공하는 든든한 파트너가 되겠습니다.')}
                </span>
                <span>
                  {t('프롭메이트와 함께라면,')}<br />
                  {t('혁신적인 기술과 신뢰할 수 있는 전문성을 통해 고객의 꿈을 현실로 만들어 드립니다.')}
                </span>
              </p>
            </div>
            <div className="info-bottom">
              <div className="download">
                <p>
                  <span>{t('Download')}</span>
                </p>
                <ul className="file_wrap" ref={fileWrapRef}>
                  {IR_FILES.map((f) => (
                    <li key={f.lang}>
                      <div className="file_img">
                        <img src={process.env.PUBLIC_URL + 'images/company/about-ir-cover.jpg'} alt={t("회사소개파일이미지")} />
                      </div>
                      <a href={process.env.PUBLIC_URL + 'files/ir/propmate-ir-' + f.lang + '.pdf'} target="_blank" rel="noopener noreferrer" className="file_opacity" lang={f.lang} dir={f.lang === 'ar' ? 'rtl' : undefined}>
                        <h3>{f.title}</h3>
                        <p>{f.label}</p>
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="file_scrollbar" ref={trackRef} aria-hidden="true">
                  <div className="file_scrollbar_thumb" ref={thumbRef}></div>
                </div>
              </div>
              <div className="sns-wrap">
                <div className="insta-box">
                 <a href="#">
                    <span>Instagram</span>
                    <img src={process.env.PUBLIC_URL + 'images/company/about-insta-img.png'} alt="insta" />
                 </a>
                </div>
                <div className="youtube-box">
                  <a href="https://www.youtube.com/@Propmate_inc" target="_blank" >
                    <span>youtube</span>
                    <img src={process.env.PUBLIC_URL + 'images/company/about-youtube-img.png'} alt="youtube" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <section className="global_container">
          <div className="global_image">
            <img src={process.env.PUBLIC_URL + 'images/company/about-taiwan.jpg'} alt={t("프롭메이트 대만 화면")} />
          </div>
          <div className="global_text">
            <span className="global_label">GLOBAL</span>
            <h2>PROP.MATE Taiwan</h2>
            <p>
              <span>
                {t('프롭메이트 대만 버전은 신주시(新竹市)를 시작으로 대만의 필지 정보를 지도 위에서 바로 확인할 수 있는 서비스입니다.')}
              </span>
              <span>
                {t('필지를 클릭하면 용도지역·건폐율·용적률과 건축 가능 규모를 보여주고,')}<br />
                {t('실거래 정보와 AI 분석으로 예상 공사비까지 한 번에 살펴볼 수 있습니다.')}
              </span>
            </p>
            <a className="global_btn" href="https://taiwan.propmate.kr" target="_blank" rel="noopener noreferrer">
              {t('대만 버전 바로가기')}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>
      </main >
      <div></div>

      {
        modalState &&

        <div className="modal">
          <div className="modal_content">
            <div className="title">{t('회사소개서 언어 선택')}</div>
            <div className="btn_wrap">
              <a className="korea">{t('한국어 🇰🇷')}</a>
              <a className="english">English 🇺🇸</a>
            </div>
            <div className="close_btn" onClick={() => { setModalState(false); }}></div>
          </div>
        </div>

      }
    </div >
  );
}

export default About;